/**
 * Clients → All clients — prototype `VIEWS.book` with its boxes override (`CBV`): the actions row
 * (managers), the filter bar (View Boxes/Table · Priority with counts · Health), then either the client
 * boxes or the table. Prototype `byPrio()`: highest priority first, then the largest revenue (when the
 * caller can see revenue), then name. The hub hides the prototype's stats strip
 * (`.hubbody:not(.hub-money) .stats{display:none}`), so it is not rendered here.
 *
 * Data: GET /clients, every page (useClients). Filters run on that list. Manager actions:
 *  - "Add a client" — capability `clients.create` → POST /clients.
 *  - "Edit priority and revenue" — capability `clients.edit.all`. Turns priority into a select and revenue
 *    into an input on every box/row; each change is PATCH /clients/{id} with If-Match. Outside edit mode
 *    the priority chip opens the priority pop-up (prototype `editClient()`).
 * A box or row opens the client panel (GET /clients/{id} + /spocs). View, filters and the open client
 * live in the address (?view=table&p=high&h=watch&client=<id>) so Back and reload keep them.
 *
 * Cross-sell: GET /opportunities (useCrossSellCounts — prototype `xsell(c)`, the client's opportunities not yet
 * closed): "N cross-sell" on the box after the priority chip, and the table's Cross-sell column ("N in progress",
 * else "None"). The server scopes a member to their own clients, so the count matches what they can see.
 * "None" only once that query has answered: "…" while it loads, "—" plus one note on the page if it failed.
 *
 * Revenue shows only when `annual_revenue_lakh` is present in the row (absent for members, ADR §11.1).
 * Usage shows only when `weighted_util` is not null; health shows "No score yet" until step 6.
 */
import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { useSearchParams } from 'react-router';
import type { ClientRow, HealthBand, Priority } from '@amplispot/contracts';
import { useSession } from '../../lib/session';
import { useClients } from '../../lib/data';
import { useCrossSellCounts } from '../../lib/opportunities';
import { CrossSellCell, CrossSellChip, crossSellStatus, type CrossSellStatus } from './crossSell';
import { firstName, monthYear, rupees } from '../../lib/format';
import { Avatar, Empty, ErrorState, HealthChip, Loading, Pills, PriorityChip, priorityLabel } from '../../ui/kit';
import { Icon } from '../../ui/Icon';
import { ClientDrawer } from './ClientDrawer';
import { MeetingDrawer } from './Meetings';
import { AddClientModal, EditPriorityModal } from './ClientModals';
import { ClientLogo, LogoWithDot, bandEdge, meterTone, useClientPatch } from './parts';
import { BulkRoutinesModal } from '../checklists/Routine';

type View = 'boxes' | 'table';
type PrioF = 'all' | Priority;
type HealthF = 'all' | HealthBand;

const PRIO_ORDER: Record<Priority, number> = { high: 0, med: 1, low: 2 };
const PRIOS: Priority[] = ['high', 'med', 'low'];

const pick = <T extends string>(v: string | null, allowed: readonly T[], fallback: T): T => (allowed.includes(v as T) ? (v as T) : fallback);

export function ClientBook() {
  const { can } = useSession();
  const clients = useClients();
  const { counts: xsell, query: xsellQ } = useCrossSellCounts();
  const xst = crossSellStatus(xsellQ);
  const [params, setParams] = useSearchParams();
  const view = pick<View>(params.get('view'), ['boxes', 'table'], 'boxes');
  const pf = pick<PrioF>(params.get('p'), ['all', 'high', 'med', 'low'], 'all');
  const hf = pick<HealthF>(params.get('h'), ['all', 'healthy', 'watch', 'at_risk'], 'all');
  const openId = params.get('client');
  // The link on a task made from minutes: /clients/book?client=…&tab=meetings&meeting=…
  const meetingId = params.get('meeting');

  const canCreate = can('clients.create');
  const canEditAll = can('clients.edit.all');
  const [editing, setEditing] = useState(false);
  const [modal, setModal] = useState<{ kind: 'add' } | { kind: 'prio'; id: string } | { kind: 'bulk' } | null>(null);
  const ed = editing && canEditAll;

  const set = (k: string, v: string | null, dflt?: string) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (v === null || v === dflt) n.delete(k);
        else n.set(k, v);
        return n;
      },
      { replace: k !== 'client' },
    );

  const all = clients.data ?? [];
  const book = useMemo(() => {
    const total = all.reduce((s, c) => s + (c.annual_revenue_lakh ?? 0), 0);
    const count = (p: Priority) => all.filter((c) => c.priority === p).length;
    return { total, counts: { high: count('high'), med: count('med'), low: count('low') } };
  }, [all]);
  const rows = useMemo(
    () =>
      all
        .filter((c) => (pf === 'all' || c.priority === pf) && (hf === 'all' || c.band === hf))
        .sort(
          (a, b) =>
            PRIO_ORDER[a.priority] - PRIO_ORDER[b.priority] ||
            (b.annual_revenue_lakh ?? 0) - (a.annual_revenue_lakh ?? 0) ||
            a.name.localeCompare(b.name),
        ),
    [all, pf, hf],
  );
  const share = (c: ClientRow) => (c.annual_revenue_lakh != null && book.total > 0 ? Math.round((c.annual_revenue_lakh / book.total) * 100) : null);

  const open = (id: string) => {
    if (!ed) set('client', id);
  };
  const editPrio = canEditAll && !ed ? (id: string) => setModal({ kind: 'prio', id }) : undefined;

  return (
    <>
      {canCreate || canEditAll ? (
        <div className="ph" style={{ margin: '0 0 12px', justifyContent: 'flex-end' }}>
          <div className="r" style={{ marginLeft: 0 }}>
            {canEditAll ? (
              // Prototype head('All clients', …): isMgr() → bulkRoutine(). POST /routines/bulk (pick or sheet).
              <button className="btn" onClick={() => setModal({ kind: 'bulk' })}>
                Recurring tasks for many clients
              </button>
            ) : null}
            {canEditAll ? (
              <button className={`btn${ed ? ' pri' : ''}`} onClick={() => setEditing((e) => !e)} aria-pressed={ed}>
                {ed ? 'Done editing' : 'Edit priority and revenue'}
              </button>
            ) : null}
            {canCreate ? (
              <button className="btn pri" onClick={() => setModal({ kind: 'add' })}>
                <Icon name="plus" /> Add a client
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="bar">
        <span className="lbl">View</span>
        <Pills<View> options={[['boxes', 'Boxes'], ['table', 'Table']]} value={view} onChange={(v) => set('view', v, 'boxes')} />
        <span style={{ width: 14 }} />
        <span className="lbl">Priority</span>
        <Pills<PrioF>
          options={[['all', 'All'], ...PRIOS.map((p): [PrioF, string, number] => [p, priorityLabel(p), book.counts[p]])]}
          value={pf}
          onChange={(v) => set('p', v, 'all')}
        />
        <div className="grow" />
        <span className="lbl">Health</span>
        <Pills<HealthF>
          options={[['all', 'All'], ['healthy', 'Healthy'], ['watch', 'Watch'], ['at_risk', 'At risk']]}
          value={hf}
          onChange={(v) => set('h', v, 'all')}
        />
      </div>

      {ed ? (
        <div className="note" style={{ marginBottom: 12 }}>
          Change a priority or a revenue figure and it saves straight away. Press <b>Done editing</b> when you are finished.
        </div>
      ) : null}

      {xst === 'error' && all.length > 0 ? (
        <div className="note" role="status" style={{ marginBottom: 12 }}>
          Cross-sell work could not be loaded, so it is not shown.{' '}
          <button className="lnk" onClick={() => xsellQ.refetch()}>
            Try again
          </button>
        </div>
      ) : null}

      {clients.isPending ? (
        <div className="panel">
          <Loading label="Loading clients…" />
        </div>
      ) : clients.isError ? (
        <div className="panel">
          <ErrorState error={clients.error} onRetry={() => clients.refetch()} />
        </div>
      ) : all.length === 0 ? (
        <div className="panel">
          <Empty title={canCreate ? 'No clients yet' : 'No clients are yours yet'}>
            {canCreate ? 'Add the first client and choose its SPOC.' : 'Clients show here once you are made their SPOC.'}
          </Empty>
        </div>
      ) : rows.length === 0 ? (
        <div className="panel">
          <Empty title="No clients match these filters">
            {hf !== 'all' && all.every((c) => c.band === null) ? 'Health scores start once usage and activity are recorded. ' : ''}
            <button className="lnk" onClick={() => setParams((p) => { const n = new URLSearchParams(p); n.delete('p'); n.delete('h'); return n; }, { replace: true })}>
              Show all clients
            </button>
          </Empty>
        </div>
      ) : view === 'boxes' ? (
        <div className="cgrid">
          {rows.map((c) => (
            <ClientBox key={c.id} c={c} share={share(c)} xsell={xsell.get(c.id) ?? 0} xst={xst} ed={ed} onOpen={() => open(c.id)} onPrio={editPrio} />
          ))}
        </div>
      ) : (
        <ClientTable rows={rows} share={share} xsell={xsell} xst={xst} ed={ed} onOpen={open} onPrio={editPrio} />
      )}

      {meetingId ? (
        <MeetingDrawer key={meetingId} id={meetingId} onClose={() => set('meeting', null)} onOpen={(id) => set('meeting', id)} />
      ) : openId ? (
        <ClientDrawer
          clientId={openId}
          tab={params.get('tab')}
          onClose={() =>
            setParams((p) => {
              const n = new URLSearchParams(p);
              n.delete('client');
              n.delete('tab');
              return n;
            })
          }
          onOpenMeeting={(id) => set('meeting', id)}
        />
      ) : null}
      {modal?.kind === 'add' ? (
        <AddClientModal
          onClose={() => setModal(null)}
          onCreated={(id) => {
            setModal(null);
            set('client', id);
          }}
        />
      ) : null}
      {modal?.kind === 'prio' ? <EditPriorityModal clientId={modal.id} onClose={() => setModal(null)} /> : null}
      {modal?.kind === 'bulk' ? <BulkRoutinesModal onClose={() => setModal(null)} /> : null}
    </>
  );
}

// ---------------------------------------------------------------- shared bits

const onKeyOpen = (fn: () => void) => (e: KeyboardEvent) => {
  if (e.target !== e.currentTarget) return;
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    fn();
  }
};

const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();

/** Edit mode: priority as a select — saves on change (prototype `inlPrio`). */
function InlinePriority({ c }: { c: ClientRow }) {
  const patch = useClientPatch();
  const [busy, setBusy] = useState(false);
  return (
    <select
      className="in"
      style={{ width: 'auto', padding: '4px 8px' }}
      value={c.priority}
      disabled={busy}
      aria-label={`Priority for ${c.name}`}
      onClick={stop}
      onChange={async (e) => {
        const p = e.target.value as Priority;
        setBusy(true);
        await patch(c, { priority: p }, `${c.name} is now ${priorityLabel(p).toLowerCase()} priority`);
        setBusy(false);
      }}
    >
      {PRIOS.map((p) => (
        <option key={p} value={p}>
          {priorityLabel(p)}
        </option>
      ))}
    </select>
  );
}

/** Edit mode: annual revenue in lakh — saves when you leave the box or press Enter (prototype `inlRev`). */
function InlineRevenue({ c }: { c: ClientRow }) {
  const patch = useClientPatch();
  const initial = c.annual_revenue_lakh == null ? '' : String(c.annual_revenue_lakh);
  const [v, setV] = useState(initial);
  // The row reloaded (our own save, or a 412 showing the other manager's value): show what the server has.
  useEffect(() => setV(initial), [initial]);
  // Compare numbers, not text: "96.50" saved as 96.5 must not count as a change on the next blur.
  const same = (a: string, b: string) => (a.trim() === '' || b.trim() === '' ? a.trim() === b.trim() : Number(a) === Number(b));
  const [bad, setBad] = useState(false);
  const [busy, setBusy] = useState(false);
  const commit = async () => {
    if (same(v, initial)) {
      setV(initial);
      return setBad(false);
    }
    const n = v.trim() === '' ? null : Number(v);
    if (n !== null && (!Number.isFinite(n) || n < 0)) return setBad(true);
    setBad(false);
    setBusy(true);
    const ok = await patch(c, { annual_revenue_lakh: n }, n === null ? `Revenue cleared for ${c.name}` : `${c.name}: ${rupees(n)} a year`);
    setBusy(false);
    if (!ok) setV(initial);
  };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }} onClick={stop}>
      <input
        className="in num"
        type="number"
        min={0}
        step="any"
        inputMode="decimal"
        value={v}
        disabled={busy}
        aria-label={`Annual revenue for ${c.name}, in lakh`}
        aria-invalid={bad || undefined}
        title={bad ? 'Enter the amount in lakh as a number, for example 96' : undefined}
        style={{ width: 90, textAlign: 'right', padding: '4px 8px' }}
        onChange={(e) => setV(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
      />
      <span className="sub">L</span>
    </span>
  );
}

/** Prototype tile footer: "3 open, 1 late" (the late part only when something is late). */
function openText(open: number, late: number) {
  return (
    <span>
      {open} open
      {late ? (
        <>
          , <b style={{ color: 'var(--cold, #DC2626)' }}>{late} late</b>
        </>
      ) : null}
    </span>
  );
}

function contactText(days: number | null): string {
  if (days === null) return 'No contact yet';
  return days === 0 ? 'Wrote today' : `Wrote ${days}d ago`;
}

// ---------------------------------------------------------------- boxes

function ClientBox(props: { c: ClientRow; share: number | null; xsell: number; xst: CrossSellStatus; ed: boolean; onOpen: () => void; onPrio?: (id: string) => void }) {
  const { c, ed } = props;
  const u = c.weighted_util;
  const hasRevenue = c.annual_revenue_lakh !== undefined;
  return (
    <div
      className="panel cbox"
      style={{ borderTop: `4px solid ${bandEdge(c.band)}`, cursor: ed ? 'default' : 'pointer' }}
      role={ed ? undefined : 'button'}
      tabIndex={ed ? undefined : 0}
      aria-label={ed ? undefined : `Open ${c.name}`}
      onClick={props.onOpen}
      onKeyDown={onKeyOpen(props.onOpen)}
    >
      <div className="bd" style={{ display: 'grid', gap: 12 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <ClientLogo id={c.id} name={c.name} size={48} />
          <div className="grow" style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.25, overflowWrap: 'anywhere' }}>{c.name}</div>
            <div className="sub" style={{ fontSize: 12.5 }}>
              {c.segment}, since {monthYear(c.since)}
            </div>
          </div>
          <HealthChip band={c.band} score={c.health?.value ?? null} />
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {ed ? <InlinePriority c={c} /> : <PriorityChip p={c.priority} onClick={props.onPrio ? () => props.onPrio!(c.id) : undefined} />}
          <CrossSellChip n={props.xsell} status={props.xst} />
          {c.open_escalations ? (
            <span className="chip cold">
              {c.open_escalations} escalation{c.open_escalations > 1 ? 's' : ''}
            </span>
          ) : null}
        </div>
        {hasRevenue ? (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span className="num" style={{ fontSize: 19, fontWeight: 700 }}>
              {ed ? <InlineRevenue c={c} /> : c.annual_revenue_lakh == null ? <span className="sub" style={{ fontSize: 14, fontWeight: 600 }}>Revenue not set</span> : rupees(c.annual_revenue_lakh)}
            </span>
            {props.share !== null ? (
              <span className="sub" style={{ fontSize: 12 }}>
                a year, {props.share}% of the book
              </span>
            ) : null}
          </div>
        ) : null}
        {u === null ? null : (
          <div className="split" title="Share of the plan being used">
            <span className="sub" style={{ fontSize: 12, width: 62 }}>
              Usage
            </span>
            <div className={`meter grow ${meterTone(u)}`}>
              <i style={{ width: `${u}%` }} />
            </div>
            <span className="num" style={{ fontSize: 12 }}>
              {Math.round(u)}%
            </span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--sub)', borderTop: '1px solid var(--line)', paddingTop: 10 }}>
          <span style={{ display: 'flex', gap: 6, alignItems: 'center', minWidth: 0 }}>
            <Avatar user={c.spoc} size={26} /> {firstName(c.spoc.name)}
          </span>
          {openText(c.open_task_count, c.overdue_count)}
          <span>{contactText(c.last_contact_days)}</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- table

function ClientTable(props: { rows: ClientRow[]; share: (c: ClientRow) => number | null; xsell: Map<string, number>; xst: CrossSellStatus; ed: boolean; onOpen: (id: string) => void; onPrio?: (id: string) => void }) {
  const { rows, ed } = props;
  const revenue = rows.some((c) => c.annual_revenue_lakh !== undefined);
  return (
    <div className="panel" style={{ overflowX: 'auto' }}>
      <table>
        <thead>
          <tr>
            <th>Client</th>
            <th>Priority</th>
            {revenue ? (
              <>
                <th className="tr">Annual revenue</th>
                <th className="tr">Share</th>
              </>
            ) : null}
            <th>Cross-sell</th>
            <th>SPOC</th>
            <th>Utilisation</th>
            <th>Open tasks</th>
            <th>Last contact</th>
            <th>Health</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const u = c.weighted_util;
            const s = props.share(c);
            const d = c.last_contact_days;
            const xs = props.xsell.get(c.id) ?? 0;
            return (
              <tr
                key={c.id}
                className="row"
                style={ed ? { cursor: 'default' } : undefined}
                tabIndex={ed ? undefined : 0}
                aria-label={ed ? undefined : `Open ${c.name}`}
                onClick={() => props.onOpen(c.id)}
                onKeyDown={onKeyOpen(() => props.onOpen(c.id))}
              >
                <td>
                  <div className="split">
                    <LogoWithDot id={c.id} name={c.name} band={c.band} />
                    <div>
                      <b>{c.name}</b>
                      <div className="sub">
                        {c.segment}, since {monthYear(c.since)}
                      </div>
                    </div>
                  </div>
                </td>
                <td>{ed ? <InlinePriority c={c} /> : <PriorityChip p={c.priority} onClick={props.onPrio ? () => props.onPrio!(c.id) : undefined} />}</td>
                {revenue ? (
                  <>
                    <td className="tr">{ed ? <InlineRevenue c={c} /> : c.annual_revenue_lakh == null ? <span className="sub">Not set</span> : <span className="num">{rupees(c.annual_revenue_lakh)}</span>}</td>
                    <td className="tr num">{s === null ? '' : `${s}%`}</td>
                  </>
                ) : null}
                <td>
                  <CrossSellCell n={xs} status={props.xst} />
                </td>
                <td>
                  <span className="split" style={{ gap: 6 }}>
                    <Avatar user={c.spoc} size={26} />
                    <span>{firstName(c.spoc.name)}</span>
                  </span>
                </td>
                <td style={{ minWidth: 120 }}>
                  {u === null ? (
                    <span className="sub">no data</span>
                  ) : (
                    <div className="split">
                      <div className={`meter grow ${meterTone(u)}`}>
                        <i style={{ width: `${u}%` }} />
                      </div>
                      <span className="num" style={{ fontSize: 12 }}>
                        {Math.round(u)}%
                      </span>
                    </div>
                  )}
                </td>
                <td className="num">{openText(c.open_task_count, c.overdue_count)}</td>
                <td>{d === null ? <span className="sub">None yet</span> : <span className={`chip${d > 14 ? ' cold' : d > 7 ? ' warm' : ''}`}>{d}d ago</span>}</td>
                <td>
                  <HealthChip band={c.band} score={c.health?.value ?? null} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
